import { Opportunity, Team } from "@entities";
import ContentCopy from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import useCreateHyperTable from "@shared/frontend/hyper-table";
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { projectAidRequestUrl } from "@shared/helpers/constants";
import { type SingleRowAction, type SingleRowActionGetter } from "@shared/frontend/hyper-table/inner-components/hyper-table-menu-actions-column/HyperTableMenuActionsColumn";
import { Roles } from "@shared/Roles";
import TeamCard, {
  type TeamData,
} from "clients/admin/src/desktop/components/popups/team-card/TeamCard";
import { useOrganization } from "clients/admin/src/desktop/contexts/OrganizationProvider";
import { useUser } from "clients/admin/src/desktop/contexts/UserProvider";
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { remult } from "remult";
import { TableHeaderTitles } from "../../table-header/TableHeader";
import { TableToolbarTabs } from "../../table-tabs/TableToolbarTabs";
import { type Tabs } from "../UsersTables";
import columns from "./columns";
import { opportunityRepo, teamRepo } from "@repositories";
import { useAlert } from "clients/admin/src/desktop/contexts/AlertProvider";
import useQuestion from "clients/admin/src/desktop/dialog-components/QuestionDialog";
import { useDeleteTeam } from "clients/admin/src/desktop/queries/communities.query";

type TeamsTableProps = Tabs

const TeamsTable: React.FC<TeamsTableProps> = (props) => {
  const { associationId } = useParams<"associationId">();
  const { activeTab = null, setActiveTab = () => { }, options = [] } = props;
  const { currOrganization } = useOrganization();
  const { currentUser } = useUser();
  const { HyperTable } = useCreateHyperTable(Team);
  const [rowData, setRowData] = useState<TeamData>();
  const [isAdd, setIsAdd] = useState<boolean>(false);
  const { setAlertText, setSeverity, setTime } = useAlert();
  const question = useQuestion();
  const navigate = useNavigate();
  const { mutateAsync: deleteTeams } = useDeleteTeam();

  const getMenuActions = useCallback<SingleRowActionGetter<Team>>((selectedRow) => {
    const actions: SingleRowAction<Team>[] = [
      {
        icon: <EditIcon />,
        text: "עריכה",
        onClick: async (e, row) => {
          const team = await teamRepo.findId(row.id, {
            include: {
              managers: { include: { manager: true } },
              parentTeam: true,
            },
          });
          if (!team) return;
          setRowData({
            ...team,
            managers: team.managers?.map((cm) => cm.manager?.id || "") || [],
            parentTeam: team.parentTeam?.teamId,
            subCommunities: getAllSubCommunityIds(team),
          });
        },
      },
    ];

    if (!selectedRow.original.isMainOrganizationTeam) {
      actions.push({
        icon: <ContentCopy />,
        text: "העתק קישור למבקשי סיוע",
        onClick: async (_e, row) => {
          await navigator.clipboard.writeText(
            projectAidRequestUrl(row.original.id),
          );
          setAlertText("הקישור הועתק ללוח");
          setSeverity("success");
        },
      });
    }

    actions.push({
        icon: <DeleteOutlineRoundedIcon />,
        text: "מחיקה",
        onClick: async (e, row) => {
          if (!remult.isAllowed(Roles.SuperAdmins)) {
            setAlertText("אין לך הרשאות למחוק פרויקט זה");
            setSeverity("warning");
            return;
          }
          if (row.original.isMainOrganizationTeam) {
            setAlertText("לא ניתן למחוק פרויקט מסוג כל המתנדבים");
            setSeverity("warning");
            return;
          }
          question("האם ברצונך למחוק פרויקט זה?").then(async (yes) => {
            if (!yes) return;
            const exclusiveOpportunityCount = await opportunityRepo.count(
              Opportunity.hyperFilter({
                isClosed: false,
                status: "APPROVED",
                teams: {
                  $every: {
                    teamId: row.original.id,
                  },
                  $some: {
                    teamId: row.original.id,
                  }
                }
              }),
            );
            if (exclusiveOpportunityCount > 0) {
              setAlertText("לא ניתן למחוק פרויקט זו מכיוון שיש לה משימות שמשויכות רק אליה, יש להחליף את הפרויקטים במשימות לפני מחיקה");
              setTime(6000);
              return;
            }
            try {
              await deleteTeams({ teamIds: [row.original.id] });
              HyperTable.context.current?.refetch("remove");
            } catch {
              // useDeleteTeam already surfaces the error
            }
          });
        },
    });

    return actions;
  }, [deleteTeams, question, setAlertText, setSeverity, setTime, HyperTable]);

  function getAllSubCommunityIds(row: Team): string[] {
    const ids: string[] = [];
    const collectIds = (team: Team) => {
      team.subTeams.forEach(({ subTeam }) => {
        if (subTeam) {
          ids.push(subTeam.id);
          collectIds(subTeam);
        }
      });
    };
    collectIds(row);
    return ids;
  }

  return (
    <HyperTable
      findConfig={{
        where: Team.hyperFilter({
          organization: {
            $id: currOrganization?.id,
          },
          id: remult.isAllowed(Roles.Admins)
            ? undefined
            : currentUser?.teams?.map((team) => team.teamId) || [],
          parentTeam: {
            $is: null,
          },
        }),
        include: {
          subTeams: {
            include: {
              subTeam: {
                include: {
                  managers: {
                    include: {
                      manager: true,
                    },
                  },
                },
              },
            },
          },
        },
      }}
      components={{
        addRowButton: {
          onClick: () => setIsAdd(true),
          content: "הוספת פרויקט",
        },
      }}
      onRowClick={(_, row) => navigate(`teams/${row.original?.id}`)}
      tableId="teams-table"
      title={
        <TableHeaderTitles
          titles={[
            { content: "מתנדבים", onClick: () => setActiveTab("volunteers") },
            { content: "פרויקטים" },
          ]}
        />
      }
      tableSettings={{
        getSubRows: (row) => {
          return row.subTeams
            .filter((item) => !!item.subTeam)
            .map((item) => item.subTeam!);
        },
      }}
      onDataFirstLoad={(ctx) => {
        if (associationId) {
          try {
            const row = ctx.table.getRow(associationId, true);
            const team = row.original;
            setRowData({
              ...team,
              managers: team.managers?.map((cm) => cm.manager?.id || "") || [],
              parentTeam: team.parentTeam as unknown as string,
              subCommunities: getAllSubCommunityIds(team),
            });
            row.toggleExpanded(true);
          } catch (error) {
            return;
          }
        }
      }}
    >
      <HyperTable.Toolbar>
        <TableToolbarTabs
          value={activeTab}
          onChange={setActiveTab}
          options={options}
        />
      </HyperTable.Toolbar>
      <HyperTable.ExpandColumn />
      {columns.map((column) => (
        <HyperTable.Column key={column.field} {...column} />
      ))}
      <HyperTable.MenuColumn defaultPinned={false} actions={getMenuActions} />
      <TeamCard
        table={HyperTable}
        isAdd={isAdd}
        setIsAdd={setIsAdd}
        defaultTeamData={rowData}
      />
    </HyperTable>
  );
};

export default TeamsTable;
